import React, { useEffect, useReducer, ChangeEvent } from 'react';
import { useIntl } from 'react-intl';
import Rating from '@mui/material/Rating';
import feedbackModalMessages from './messages';

import './FeedbackModal.scss';

const { getAuthenticatedHttpClient } = require('@edx/frontend-platform/auth');
const { getConfig } = require('@edx/frontend-platform');

const {
  useToggle, Button, Form, ActionRow, ModalDialog,
} = require('@edx/paragon');

interface State {
  title: string;
  ratingContent: number;
  ratingInstructors: number;
  recommended: boolean;
  public: boolean;
  feedback: string;
  showForm: boolean,
  message:string;
}

interface Action {
  type: string;
  title: string;
  ratingContent: number;
  ratingInstructors: number;
  recommended: boolean;
  public: boolean;
  feedback: string;
  message:string;
}

interface Data {
  data: {
    attributes: {
      rating_content: number,
      rating_instructors: number,
      recommended: boolean,
      public: boolean;
      feedback: string,
    }
  }
}

interface OnSuccess {
  (data?: Object): void;
}

interface OnFailure {
  (error?: Error): void;
}

/**
 * Function that specifies how the state gets updated.
 * @param  {State} state   Current state.
 * @param  {Action} action Describes kind of action to be executed.
 * @return {Object}        Updated state based on the action type.
 */
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_FEEDBACK':
      return {
        ...state,
        feedback: action.feedback,
      };
    case 'SET_RATING_CONTENT':
      return {
        ...state,
        ratingContent: action.ratingContent,
      };
    case 'SET_RATING_INSTRUCTORS':
      return {
        ...state,
        ratingInstructors: action.ratingInstructors,
      };
    case 'SET_RECOMMENDED':
      return {
        ...state,
        recommended: action.recommended,
      };
    case 'SET_PUBLIC':
      return {
        ...state,
        public: action.public,
      };
    case 'SHOW_MESSAGE':
      return {
        ...state,
        showForm: false,
        title: action.title,
        message: action.message,
      };
    default:
      return state;
  }
}

/**
 * This makes a GET request and allow to execute a function in case on success or on failure.
 * @param  {String}       path       String that contains the API Path the base url is COURSE_EXPERIENCE_API_URL.
 * @param  {Object}       body       Body of the POST request.
 * @param  {Object}       apiConfig  Extra configuration, likes headers.
 * @param  {OnSuccess}    onSuccess  Function that will be executed on 20X responses.
 * @param  {OnFailure}    onFailure  Function that will be executed on responses different from 20X responses.
 */
function getData(path: string, apiConfig: Object, onSuccess: OnSuccess, onFailure: OnFailure) {
  const { COURSE_EXPERIENCE_API_URL } = getConfig();
  const url = `${COURSE_EXPERIENCE_API_URL}/${path}/`;

  getAuthenticatedHttpClient().get(url, apiConfig).then(({ data }: { data: Data }) => {
    onSuccess(data);
  }, (error: Error) => {
    onFailure(error);
  });
}

/**
 * This makes a POST request and allow to execute a function in case on success or on failure.
 * @param  {String}       path       String that contains the API Path the base url is COURSE_EXPERIENCE_API_URL.
 * @param  {Object}       body       Body of the POST request.
 * @param  {Object}       apiConfig  Extra configuration, likes headers.
 * @param  {OnSuccess}    onSuccess  Function that will be executed on 20X responses.
 * @param  {OnFailure}    onFailure  Function that will be executed on responses different from 20X responses.
 */
function postData(path: string, body: Object, apiConfig: Object, onSuccess: OnSuccess, onFailure: OnFailure) {
  const { COURSE_EXPERIENCE_API_URL } = getConfig();
  const url = `${COURSE_EXPERIENCE_API_URL}/${path}/`;

  getAuthenticatedHttpClient().post(url, body, apiConfig).then(({ data }: { data: Data }) => {
    onSuccess(data);
  }, (error: Error) => {
    onFailure(error);
  });
}

/**
 * Function react component that allows to upload a . This allows to rate the content
 * and instructors, also allows to upload a specific feedback and recommends the course.
 * @param  {String}         courseId   Course identifier as string.
 * @param  {Boolean}        showModal  This show will display the feedback form.
 * @return {JSX.Element}               React element.
 */
function FeedbackModal({ courseId, showModal }: { courseId: string, showModal: boolean }): JSX.Element {
  const intl = useIntl();
  const initialState = {
    title: intl.formatMessage(feedbackModalMessages.congratulationsTitle),
    ratingContent: 0,
    ratingInstructors: 0,
    recommended: true,
    public: true,
    feedback: '',
    showForm: true,
    message: '',
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isOpen, open, close] = useToggle(false);
  const feedbackApiConfig = { headers: { accept: 'application/vnd.api+json,application/json' } };

  // This allows to save the user feedback
  const submit = () => {
    const feedbackPath = 'feedback/courses';
    const body = {
      rating_content: state.ratingContent,
      rating_instructors: state.ratingInstructors,
      recommended: state.recommended,
      public: state.public,
      feedback: state.feedback,
      course_id: { type: 'CourseOverview', id: courseId },
    };
    const postOnSuccess = () => {
      // This action will set the success state message and active the message section.
      dispatch({ type: 'SHOW_MESSAGE', ...state, message: intl.formatMessage(feedbackModalMessages.successMessage) });
    };
    const postOnFailure = () => {
      // This action will set the error state message and active the message section.
      dispatch({
        type: 'SHOW_MESSAGE', ...state, title: intl.formatMessage(feedbackModalMessages.errorTitle), message: intl.formatMessage(feedbackModalMessages.errorMessage),
      });
    };
    // Save user feedback.
    postData(feedbackPath, body, feedbackApiConfig, postOnSuccess, postOnFailure);
  };

  useEffect(() => {
    const feedbackPath = `feedback/courses/${courseId}`;
    const getOnSuccess = () => {
      // On success the modal will be closed since that mean that the records already exists in the database
      close();
    };
    const getOnFailure = () => {
      // On failure the modal will be opened, the on failure case means that the records was not found therefore
      // the modal must be shown
      open();
    };
    // Verify is the user has already submitted any feedback.
    getData(feedbackPath, feedbackApiConfig, getOnSuccess, getOnFailure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  return (
    <ModalDialog
      title="Feedback Dialog"
      isOpen={isOpen && showModal}
      onClose={close}
      size="md"
      variant="success"
      className="feedback-modal"
      hasCloseButton
      isFullscreenOnMobile
    >
      <ModalDialog.Header>
        <ModalDialog.Title>
          {state.title}
        </ModalDialog.Title>
      </ModalDialog.Header>

      {state.showForm
        ? (
          <ModalDialog.Body>
            <p>{intl.formatMessage(feedbackModalMessages.bodyIntroduction)}</p>
            <p>{intl.formatMessage(feedbackModalMessages.bodyThanks)}</p>
            <h4>
              {intl.formatMessage(feedbackModalMessages.rateTitle)}
            </h4>
            <Form.Group>
              <Form.Label className="rating-label">{intl.formatMessage(feedbackModalMessages.content)}</Form.Label>
              <Rating
                className="rating-container content"
                name="content-starts"
                value={state.ratingContent}
                onChange={(e: ChangeEvent<HTMLButtonElement>) => { dispatch({ type: 'SET_RATING_CONTENT', ...state, ratingContent: parseInt(e.target.value, 10) }); }}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label className="rating-label">{intl.formatMessage(feedbackModalMessages.instructors)}</Form.Label>
              <Rating
                className="rating-container instructors"
                name="instructor-starts"
                value={state.ratingInstructors}
                onChange={(e: ChangeEvent<HTMLButtonElement>) => { dispatch({ type: 'SET_RATING_INSTRUCTORS', ...state, ratingInstructors: parseInt(e.target.value, 10) }); }}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label className="label">{intl.formatMessage(feedbackModalMessages.recommendMessage)}</Form.Label>
              <Form.RadioSet
                name="recommendation"
                value={state.recommended.toString()}
                onChange={(e: ChangeEvent<HTMLButtonElement>) => { dispatch({ type: 'SET_RECOMMENDED', ...state, recommended: JSON.parse(e.target.value) }); }}
              >
                <Form.Radio className="recommended-option recommended" key="yes" value="true">{intl.formatMessage(feedbackModalMessages.yes)}</Form.Radio>
                <Form.Radio className="recommended-option not-recommended" key="no" value="false">{intl.formatMessage(feedbackModalMessages.no)}</Form.Radio>
              </Form.RadioSet>
            </Form.Group>
            <Form.Group>
              <Form.Label className="label">{intl.formatMessage(feedbackModalMessages.publicMessage)}</Form.Label>
              <Form.RadioSet
                name="public"
                value={state.public.toString()}
                onChange={(e: ChangeEvent<HTMLButtonElement>) => { dispatch({ type: 'SET_PUBLIC', ...state, public: JSON.parse(e.target.value) }); }}
              >
                <Form.Radio className="public-option public" key="yes" value="true">{intl.formatMessage(feedbackModalMessages.yes)}</Form.Radio>
                <Form.Radio className="public-option not-public" key="no" value="false">{intl.formatMessage(feedbackModalMessages.no)}</Form.Radio>
              </Form.RadioSet>
            </Form.Group>
            <Form.Group controlId="formFeedbackMessage">
              <Form.Control
                as="textarea"
                maxLength="500"
                autoResize
                value={state.feedback}
                onChange={(e: ChangeEvent<HTMLButtonElement>) => { dispatch({ type: 'SET_FEEDBACK', ...state, feedback: e.target.value }); }}
                floatingLabel={intl.formatMessage(feedbackModalMessages.floatingLabel)}
              />
            </Form.Group>
          </ModalDialog.Body>
        )
        : (
          <ModalDialog.Body>
            <p>
              {state.message}
            </p>
          </ModalDialog.Body>
        )}

      <ModalDialog.Footer>
        <ActionRow>
          <ModalDialog.CloseButton variant="tertiary">
            {intl.formatMessage(feedbackModalMessages.cancel)}
          </ModalDialog.CloseButton>
          <Button
            id="submit-button"
            variant="primary"
            onClick={submit}
            disabled={!state.showForm}
          >
            {intl.formatMessage(feedbackModalMessages.submit)}
          </Button>
        </ActionRow>
      </ModalDialog.Footer>
    </ModalDialog>
  );
}

export default FeedbackModal;
